import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';

export interface Template {
  id: string;
  name: string;
  html: string;
  createdAt: string;
  updatedAt: string;
}

interface TemplatesState {
  templates: Template[];
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

export const useTemplates = () => {
  const [state, setState] = useState<TemplatesState>({
    templates: [],
    isLoading: false,
    error: null,
    success: false,
  });

  const { fetchWithAuth } = useFetch();

  // Buscar todos os templates
  const fetchTemplates = useCallback(async (): Promise<void> => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await fetchWithAuth(API_ROUTES.TEMPLATES.list());

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar templates: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const templates = data.templates || [];

      setState((prev) => ({
        ...prev,
        templates,
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  // Criar novo template
  const createTemplate = useCallback(async (name: string, html: string): Promise<boolean> => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
      success: false,
    }));

    try {
      const response = await fetchWithAuth(API_ROUTES.TEMPLATES.create(), {
        method: 'POST',
        body: JSON.stringify({ name, html }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao criar template: ${response.status} - ${errorText}`);
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        success: true,
      }));

      // Atualizar lista após criar
      await fetchTemplates();
      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
        success: false,
      }));
      return false;
    }
  }, [fetchWithAuth, fetchTemplates]);

  // Atualizar template existente
  const updateTemplate = useCallback(async (id: string, name: string, html: string): Promise<boolean> => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
      success: false,
    }));

    try {
      const response = await fetchWithAuth(API_ROUTES.TEMPLATES.update(id), {
        method: 'PUT',
        body: JSON.stringify({ name, html }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao atualizar template: ${response.status} - ${errorText}`);
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        success: true,
      }));

      // Atualizar lista após editar
      await fetchTemplates();
      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
        success: false,
      }));
      return false;
    }
  }, [fetchWithAuth, fetchTemplates]);

  // Deletar template
  const deleteTemplate = useCallback(async (id: string): Promise<boolean> => {
    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await fetchWithAuth(API_ROUTES.TEMPLATES.delete(id), {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao deletar template: ${response.status} - ${errorText}`);
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        success: true,
      }));

      // Atualizar lista após deletar
      await fetchTemplates();
      return true;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
      return false;
    }
  }, [fetchWithAuth, fetchTemplates]);

  return {
    templates: state.templates,
    isLoading: state.isLoading,
    error: state.error,
    success: state.success,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
};

